import React, { useEffect, useState } from "react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import api from "../lib/api";

/* ─── types ───────────────────────────────────────────────── */
interface DailyUsage {
  date: string;
  units_consumed: string;
  peak_flow_rate: string;
}

interface Meter {
  id: number;
  serial_number: string;
  unit_number: string;
  property_name: string;
  status: string;
}

/* ─── helpers ─────────────────────────────────────────────── */
const fmt = (n: number, d = 0) =>
  n.toLocaleString("en-KE", { minimumFractionDigits: d, maximumFractionDigits: d });

/* ─── chart tooltip ──────────────────────────────────────── */
function ChartTooltip({ active, payload, label, prefix = "", suffix = "" }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0f2557] border border-white/10 rounded-xl px-3.5 py-2.5 shadow-xl text-xs">
      <p className="text-blue-300/70 mb-1.5 font-medium">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} className="flex items-center gap-2 text-white">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }} />
          <span className="text-blue-200/70">{p.name}:</span>
          <span className="font-semibold">{prefix}{fmt(Number(p.value), 2)}{suffix}</span>
        </p>
      ))}
    </div>
  );
}

/* ─── stat card ──────────────────────────────────────────── */
function StatCard({
  label, value, sub, icon, color, trend,
}: {
  label: string; value: string; sub?: string; icon: React.ReactNode; color: string; trend?: { value: string; up: boolean };
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11.5px] text-gray-400 font-medium uppercase tracking-widest truncate">{label}</p>
        <div className="flex items-end gap-2 mt-0.5">
          <p className="text-[22px] font-bold text-gray-800 leading-tight">{value}</p>
          {trend && (
            <span className={`mb-1 text-[11px] font-semibold px-1.5 py-0.5 rounded-md ${trend.up ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
              {trend.up ? "▲" : "▼"} {trend.value}
            </span>
          )}
        </div>
        {sub && <p className="text-[11.5px] text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

/* ─── section header ─────────────────────────────────────── */
function SectionHeader({ title, sub, action }: { title: string; sub?: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between mb-4">
      <div>
        <h2 className="text-[15px] font-bold text-gray-800">{title}</h2>
        {sub && <p className="text-[12px] text-gray-400 mt-0.5">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

/* ─── skeleton ───────────────────────────────────────────── */
function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-100 rounded-xl ${className}`} />;
}

/* ─── range tab ──────────────────────────────────────────── */
function RangeTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all ${active ? "bg-sky-500 text-white shadow-sm" : "text-gray-500 hover:bg-gray-100"}`}
    >
      {label}
    </button>
  );
}

/* ─── mock generators ────────────────────────────────────── */
function genRevenue(days: number) {
  return Array.from({ length: Math.min(days, 30) }, (_, i) => {
    const d = new Date(Date.now() - (days - 1 - i) * 86400000);
    return {
      date: d.toLocaleDateString("en-KE", { day: "numeric", month: "short" }),
      revenue: Math.round(1200 + Math.random() * 2800),
      tokens: Math.round(3 + Math.random() * 12),
    };
  });
}

function genMeterComparison(meters: Meter[]) {
  return (meters.length ? meters.slice(0, 8) : [{ serial_number: "MTR-001" }, { serial_number: "MTR-002" }, { serial_number: "MTR-003" }, { serial_number: "MTR-004" }]).map((m: any) => ({
    name: m.serial_number,
    consumed: Math.round(80 + Math.random() * 200),
    revenue: Math.round(400 + Math.random() * 1200),
  }));
}

const PROPERTY_DIST = [
  { name: "Residential", value: 58 },
  { name: "Commercial", value: 24 },
  { name: "Institution", value: 12 },
  { name: "Mixed Use", value: 6 },
];

const TOP_TENANTS = [
  { name: "John Mwangi", unit: "A-102", meter: "MTR-001", consumed: 312, spend: 3120, trend: "up" },
  { name: "Grace Wanjiku", unit: "B-204", meter: "MTR-003", consumed: 287, spend: 2870, trend: "up" },
  { name: "Peter Otieno", unit: "A-305", meter: "MTR-007", consumed: 241, spend: 2410, trend: "down" },
  { name: "Mary Kamau", unit: "C-101", meter: "MTR-012", consumed: 218, spend: 2180, trend: "up" },
  { name: "David Njoroge", unit: "B-106", meter: "MTR-005", consumed: 195, spend: 1950, trend: "down" },
];

const LOW_BALANCE = [
  { name: "Unit A-102", serial: "MTR-001", balance: 4.2, threshold: 5 },
  { name: "Unit C-301", serial: "MTR-009", balance: 2.1, threshold: 5 },
  { name: "Unit B-203", serial: "MTR-006", balance: 8.4, threshold: 10 },
];

const PIE_COLORS = ["#0ea5e9", "#6366f1", "#f59e0b", "#10b981"];

/* ═══════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════ */
export default function AnalyticsPage() {
  const [meters, setMeters] = useState<Meter[]>([]);
  const [selectedMeter, setSelectedMeter] = useState<number | null>(null);
  const [usageData, setUsageData] = useState<DailyUsage[]>([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"overview" | "meters" | "revenue" | "tenants">("overview");

  const revenueData = genRevenue(days);
  const meterComp = genMeterComparison(meters);

  const totalRevenue = revenueData.reduce((s, r) => s + r.revenue, 0);
  const totalConsumed = usageData.reduce((s, d) => s + parseFloat(d.units_consumed), 0);
  const avgDaily = usageData.length ? totalConsumed / usageData.length : 0;
  const peakDay = [...usageData].sort((a, b) => parseFloat(b.units_consumed) - parseFloat(a.units_consumed))[0];

  const chartData = usageData.map((d) => ({
    date: new Date(d.date).toLocaleDateString("en-KE", { day: "numeric", month: "short" }),
    consumed: parseFloat(d.units_consumed),
    peak: parseFloat(d.peak_flow_rate),
  }));

  useEffect(() => {
    api.get("/meters/")
      .then(({ data }) => {
        const list = data.results ?? data;
        setMeters(list);
        if (list.length > 0) setSelectedMeter(list[0].id);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedMeter) return;
    api.get(`/analytics/meter/${selectedMeter}/usage/?days=${days}`)
      .then(({ data }) => setUsageData(data))
      .catch(() => setUsageData([]));
  }, [selectedMeter, days]);

  /* ── TAB NAVIGATION ── */
  const TABS = [
    { key: "overview", label: "Overview" },
    { key: "meters", label: "Meter Analytics" },
    { key: "revenue", label: "Revenue" },
    { key: "tenants", label: "Top Tenants" },
  ] as const;

  return (
    <div className="space-y-7">

      {/* ── header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-bold text-gray-800 leading-tight">Analytics</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">Consumption trends, revenue insights and meter performance</p>
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="border border-gray-200 rounded-xl px-3 py-2 text-[13px] font-medium text-gray-700 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          {meters.length > 0 && (
            <select
              value={selectedMeter ?? ""}
              onChange={(e) => setSelectedMeter(Number(e.target.value))}
              className="border border-gray-200 rounded-xl px-3 py-2 text-[13px] font-medium text-gray-700 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-300"
            >
              {meters.map((m) => (
                <option key={m.id} value={m.id}>{m.property_name} — Unit {m.unit_number}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* ── KPIs ── */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Total Revenue"
            value={`KES ${fmt(totalRevenue)}`}
            sub={`Last ${days} days`}
            trend={{ value: "12.4%", up: true }}
            color="bg-sky-50"
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth={1.8} className="w-5 h-5"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20M6 15h4" strokeLinecap="round"/></svg>}
          />
          <StatCard
            label="Units Consumed"
            value={`${fmt(totalConsumed, 1)} L`}
            sub={`Avg ${fmt(avgDaily, 1)} L/day`}
            trend={{ value: "3.1%", up: false }}
            color="bg-emerald-50"
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth={1.8} className="w-5 h-5"><path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          />
          <StatCard
            label="Active Meters"
            value={`${meters.filter((m) => m.status === "online").length}`}
            sub={`of ${meters.length} total`}
            color="bg-violet-50"
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth={1.8} className="w-5 h-5"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          />
          <StatCard
            label="Peak Day"
            value={peakDay ? new Date(peakDay.date).toLocaleDateString("en-KE", { day: "numeric", month: "short" }) : "—"}
            sub={peakDay ? `${fmt(parseFloat(peakDay.units_consumed), 1)} L consumed` : "No data yet"}
            color="bg-amber-50"
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth={1.8} className="w-5 h-5"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeLinecap="round" strokeLinejoin="round"/></svg>}
          />
        </div>
      )}

      {/* ── tab bar ── */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 rounded-lg text-[12.5px] font-semibold transition-all ${activeTab === t.key ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ══════ OVERVIEW TAB ══════ */}
      {activeTab === "overview" && (
        <>
          {/* area + pie */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <SectionHeader title="Daily Consumption" sub={`Water usage over last ${days} days`} />
              {loading ? (
                <Skeleton className="h-56" />
              ) : chartData.length === 0 ? (
                <div className="h-56 flex items-center justify-center text-[13px] text-gray-400">No data for this period.</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={chartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="consumeGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={Math.floor(chartData.length / 6)} />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip suffix=" L" />} />
                    <Area type="monotone" dataKey="consumed" name="Consumed" stroke="#0ea5e9" strokeWidth={2} fill="url(#consumeGrad)" dot={false} activeDot={{ r: 4 }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* property distribution */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <SectionHeader title="Property Mix" sub="Revenue by property type" />
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={PROPERTY_DIST} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                    {PROPERTY_DIST.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                  </Pie>
                  <Tooltip formatter={(v: any) => [`${v}%`, ""]} contentStyle={{ background: "#0f2557", border: "none", borderRadius: 10, fontSize: 12 }} itemStyle={{ color: "#e2e8f0" }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 space-y-1.5">
                {PROPERTY_DIST.map((d, i) => (
                  <div key={d.name} className="flex items-center gap-2 text-[12px]">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: PIE_COLORS[i] }} />
                    <span className="text-gray-500 flex-1">{d.name}</span>
                    <span className="font-semibold text-gray-700">{d.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* peak flow */}
          {chartData.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <SectionHeader title="Peak Flow Rate" sub="Maximum flow reading per day (L/min)" />
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={chartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="peakGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={Math.floor(chartData.length / 6)} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip suffix=" L/min" />} />
                  <Area type="monotone" dataKey="peak" name="Peak Flow" stroke="#6366f1" strokeWidth={2} fill="url(#peakGrad)" dot={false} activeDot={{ r: 4 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* low balance alerts */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
              <div>
                <h2 className="text-[14px] font-bold text-gray-800">⚠️ Low Balance Alerts</h2>
                <p className="text-[12px] text-gray-400 mt-0.5">Meters with balance below threshold — tenant top-up needed</p>
              </div>
              <span className="text-[12px] font-semibold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-lg">
                {LOW_BALANCE.length} alerts
              </span>
            </div>
            <div className="divide-y divide-gray-50">
              {LOW_BALANCE.map((alert, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-3.5">
                  <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth={1.8} className="w-4 h-4">
                      <path d="M12 9v4M12 17h.01" strokeLinecap="round"/>
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-gray-800">{alert.name} <span className="text-gray-400 font-normal">· {alert.serial}</span></p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 bg-gray-100 rounded-full h-1.5 max-w-[120px]">
                        <div className="bg-red-400 h-1.5 rounded-full" style={{ width: `${Math.min(100, (alert.balance / alert.threshold) * 100)}%` }} />
                      </div>
                      <span className="text-[11.5px] text-red-500 font-medium">{alert.balance} / {alert.threshold} units</span>
                    </div>
                  </div>
                  <span className="text-[11.5px] font-semibold text-red-500 bg-red-50 px-2.5 py-1 rounded-lg flex-shrink-0">Low</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ══════ METERS TAB ══════ */}
      {activeTab === "meters" && (
        <>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <SectionHeader title="Consumption by Meter" sub="Total units consumed per meter this period" />
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={meterComp} margin={{ top: 4, right: 8, left: -10, bottom: 0 }} barSize={20}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip suffix=" L" />} />
                <Bar dataKey="consumed" name="Consumed" fill="#0ea5e9" radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <SectionHeader title="Revenue by Meter" sub="KES generated per meter this period" />
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={meterComp} margin={{ top: 4, right: 8, left: -10, bottom: 0 }} barSize={20}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip prefix="KES " />} />
                <Bar dataKey="revenue" name="Revenue" fill="#6366f1" radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* meter status table */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50">
              <h2 className="text-[14px] font-bold text-gray-800">Meter Status Summary</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    {["Serial", "Property", "Unit", "Status", "Consumed (L)", "Revenue (KES)"].map((h) => (
                      <th key={h} className="px-5 py-3 text-[11px] text-gray-400 font-semibold uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {(meters.length ? meters : [{ id: 1, serial_number: "MTR-001", property_name: "Sunrise Apts", unit_number: "A-101", status: "online" }]).slice(0, 10).map((m, i) => (
                    <tr key={m.id ?? i} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3.5 font-medium text-gray-800">{m.serial_number}</td>
                      <td className="px-5 py-3.5 text-gray-500">{m.property_name}</td>
                      <td className="px-5 py-3.5 text-gray-500">{m.unit_number}</td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                          m.status === "online" ? "bg-emerald-50 text-emerald-600" :
                          m.status === "fault" ? "bg-red-50 text-red-500" : "bg-gray-100 text-gray-500"
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${m.status === "online" ? "bg-emerald-400" : m.status === "fault" ? "bg-red-400" : "bg-gray-400"}`} />
                          {m.status}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-gray-700 font-medium">{fmt(meterComp[i % meterComp.length]?.consumed ?? 0)}</td>
                      <td className="px-5 py-3.5 text-gray-700 font-medium">{fmt(meterComp[i % meterComp.length]?.revenue ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ══════ REVENUE TAB ══════ */}
      {activeTab === "revenue" && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-start justify-between mb-4">
                <SectionHeader title="Revenue Trend" sub="Daily token sales (KES)" />
                <div className="flex gap-1 bg-gray-50 rounded-lg p-1">
                  {[7, 30, 90].map((d) => (
                    <RangeTab key={d} label={`${d}d`} active={days === d} onClick={() => setDays(d)} />
                  ))}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={revenueData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={Math.floor(revenueData.length / 6)} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip prefix="KES " />} />
                  <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#10b981" strokeWidth={2} fill="url(#revGrad)" dot={false} activeDot={{ r: 4 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <SectionHeader title="Revenue Summary" />
              <div className="space-y-3">
                {[
                  { label: "Total Revenue", value: `KES ${fmt(totalRevenue)}`, color: "text-emerald-600" },
                  { label: "Daily Average", value: `KES ${fmt(Math.round(totalRevenue / days))}`, color: "text-sky-600" },
                  { label: "Best Day", value: `KES ${fmt(Math.max(...revenueData.map((r) => r.revenue)))}`, color: "text-violet-600" },
                  { label: "Total Tokens Sold", value: fmt(revenueData.reduce((s, r) => s + r.tokens, 0)), color: "text-amber-600" },
                ].map((s) => (
                  <div key={s.label} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                    <p className="text-[12.5px] text-gray-500">{s.label}</p>
                    <p className={`text-[14px] font-bold ${s.color}`}>{s.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <SectionHeader title="Token Sales Volume" sub="Number of tokens sold per day" />
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={revenueData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={Math.floor(revenueData.length / 6)} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip suffix=" tokens" />} />
                <Line type="monotone" dataKey="tokens" name="Tokens" stroke="#6366f1" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {/* ══════ TENANTS TAB ══════ */}
      {activeTab === "tenants" && (
        <>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50">
              <h2 className="text-[14px] font-bold text-gray-800">Top Consuming Tenants</h2>
              <p className="text-[12px] text-gray-400 mt-0.5">Ranked by water consumption this period</p>
            </div>
            <div className="divide-y divide-gray-50">
              {TOP_TENANTS.map((t, i) => (
                <div key={t.name} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50/50 transition-colors">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-[12px] font-bold text-white flex-shrink-0 ${
                    i === 0 ? "bg-amber-400" : i === 1 ? "bg-gray-400" : i === 2 ? "bg-orange-400" : "bg-gray-200 text-gray-500"
                  }`}>
                    {i + 1}
                  </div>
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center text-[13px] font-bold text-white flex-shrink-0">
                    {t.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-gray-800">{t.name}</p>
                    <p className="text-[11.5px] text-gray-400">Unit {t.unit} · Meter {t.meter}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[13px] font-bold text-gray-800">{fmt(t.consumed)} L</p>
                    <p className="text-[11.5px] text-gray-400">KES {fmt(t.spend)}</p>
                  </div>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                    t.trend === "up" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"
                  }`}>
                    {t.trend === "up" ? "▲" : "▼"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <SectionHeader title="Tenant Consumption Distribution" sub="Units consumed ranked by tenant" />
            <ResponsiveContainer width="100%" height={240}>
              <BarChart
                data={TOP_TENANTS.map((t) => ({ name: t.name.split(" ")[0], consumed: t.consumed, spend: t.spend }))}
                margin={{ top: 4, right: 8, left: -10, bottom: 0 }}
                barSize={22}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: "#64748b" }} />
                <Bar dataKey="consumed" name="Units (L)" fill="#0ea5e9" radius={[5, 5, 0, 0]} />
                <Bar dataKey="spend" name="Spend (KES)" fill="#6366f1" radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

    </div>
  );
}
